// Inspiration — a daily curated piece: a quote + an image (see docs/inspiration.md).

import type {
  InspirationDay,
  InspirationFilter,
  InspirationProgress,
  InspirationReaction,
  InspirationToday,
} from "@/library/model/wire.js";
import { api } from "@/platform/client";
import { qk } from "@/platform/client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

function invalidateInspiration(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: qk.inspiration.all });
}

/**
 * Today's piece.
 *
 * Deliberately sends no date. Unlike DailyEntryWidget — where the user is
 * logging *their* day and gets to say which one it is — here the server decides,
 * from the display-timezone setting. Letting the browser assert the day meant a
 * client one day ahead assigned tomorrow's art, running the counter ahead of the
 * gallery and burning pieces out of the pool (see days.ts:resolveToday).
 *
 * Note this GET *assigns* the day's piece server-side on its first call of a
 * day — which is what makes days you don't open the app cost nothing (see
 * docs/inspiration.md). Deliberately no `refetchInterval`: unlike feeds, nothing
 * arrives on its own, and the answer is fixed for the whole day.
 */
export function useInspirationToday() {
  return useQuery({
    queryKey: qk.inspiration.today,
    queryFn: () => api.get<InspirationToday>("/inspiration/today"),
  });
}

/** The gallery: every day already surfaced, newest first. */
export function useInspirationDays(filter: InspirationFilter = "all") {
  return useQuery({
    queryKey: qk.inspiration.days(filter),
    queryFn: () => api.get<InspirationDay[]>(`/inspiration/days?filter=${filter}`),
  });
}

/** Batch progress for the gallery's `x/y` counter. */
export function useInspirationProgress() {
  return useQuery({
    queryKey: qk.inspiration.progress,
    queryFn: () => api.get<InspirationProgress>("/inspiration/progress"),
  });
}

/** Flips a face-down card — an anchored piece whose day passed unseen. */
export function useRevealInspirationDay() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (date: string) => api.post<InspirationDay>(`/inspiration/days/${date}/reveal`),
    onSuccess: () => invalidateInspiration(qc),
  });
}

/** Three-state: "like" | "dislike" | null to clear. */
export function useSetQuoteReaction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reaction }: { id: number; reaction: InspirationReaction | null }) =>
      api.patch(`/inspiration/quotes/${id}/reaction`, { reaction }),
    onSuccess: () => invalidateInspiration(qc),
  });
}

export function useSetArtworkReaction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reaction }: { id: number; reaction: InspirationReaction | null }) =>
      api.patch(`/inspiration/artworks/${id}/reaction`, { reaction }),
    onSuccess: () => invalidateInspiration(qc),
  });
}
